import React, { useEffect, useState, useRef } from 'react';
import { Box } from '@cloudscape-design/components';
import { useTranslation } from 'react-i18next';
import Icon from "@cloudscape-design/components/icon";
import * as api from "../api/api";
// import { useAlert } from '../components/Alert';

// Define service rates constants
const SERVICE_RATES = {
  transcribe: {
    perMinute: 0.00383, // Based on actual costs
  },
  comprehend: {
    perCall: 0.0005, // Based on actual costs per call
  },
  bedrock: {
    claudeHaiku: 0.00022, // Cost per call based on actual data
  },
  athena: {
    perCall: 0.00002, // Based on actual costs
  }
};

export const DashboardMetrics = ({data}) => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState({
    totalCalls: 0,
    failedCalls: 0,
    validCallsCount: 0,
    totalCost: 0,
    costPerCall: 0,
    avgDuration: 0,
    totalMinutes: 0
  });
  // const { showSuccess, showError } = useAlert();

  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(true);
  const successShownRef = useRef(false);

  useEffect(() => {
    if (data && data.Records) {
      fetchMetricsData();
    }
  }, [data]);

  useEffect(() => {
    if (loadingRef.current === true && loading === false && !successShownRef.current) {
      successShownRef.current = true;
    }
    
    loadingRef.current = loading;
  }, [loading]);

  const fetchMetricsData = async () => {
    try {
      setLoading(true);
      successShownRef.current = false;

      const callsData = await api.getCalls();
      
      const totalCalls = callsData.totalCount || 0;
      const failedCalls = callsData.rejectedCount || 0;
      
      // Parse and validate duration values (handle string values)
      const validCalls = data.Records.filter(call => {
        // Parse duration regardless if it's string or number
        const durationValue = parseFloat(call.duration);
        // Only include if it's a valid number and greater than zero
        return !isNaN(durationValue) && durationValue > 0;
      });
      
      // Calculate total and average duration in seconds for valid calls only
      const totalDuration = validCalls.reduce((sum, call) => {
        const durationValue = parseFloat(call.duration);
        return sum + durationValue;
      }, 0);
      const avgDuration = validCalls.length > 0 ? totalDuration / validCalls.length : 0;
      
      // Calculate minutes for cost calculations
      const totalMinutes = totalDuration / 60;
      
      // Calculate costs based on actual spending pattern from AWS bill
      // Transcribe cost - scales with duration
      const transcribeCost = (totalMinutes * SERVICE_RATES.transcribe.perMinute);
      
      // Fixed costs per valid call
      const comprehendCost = validCalls.length * SERVICE_RATES.comprehend.perCall;
      const bedrockCost = validCalls.length * SERVICE_RATES.bedrock.claudeHaiku;
      const athenaCost = validCalls.length * SERVICE_RATES.athena.perCall;
      
      // Calculate total cost
      const totalCost = transcribeCost + comprehendCost + bedrockCost + athenaCost;
      
      // Calculate cost per call
      const costPerCall = validCalls.length > 0 ? totalCost / validCalls.length : 0;

      
      setMetrics({
        totalCalls,
        failedCalls,
        validCallsCount: validCalls.length,
        totalCost,
        costPerCall,
        avgDuration: avgDuration / 60,
        totalMinutes
      });
    } catch (error) {
      console.error("Error fetching metrics data:", error);
      // showError(
      //   t('notifications.errorLoading', 'Error al cargar los datos. Intente de nuevo.'),
      //   t('notifications.error', 'Error')
      // );
    } finally {
      setLoading(false);
    }
  };

  const metricCards = [
    {
      id: 'totalCalls',
      title: t('dashboardMetrics.totalCalls'),
      value: metrics.totalCalls,
      icon: 'call',
      secondaryIcon: 'refresh'
    },
    {
      id: 'failedCalls',
      title: t('dashboardMetrics.failedCalls'),
      value: metrics.failedCalls,
      icon: 'status-negative',
      secondaryIcon: 'close'
    },
    // {
    //   id: 'avgDuration',
    //   title: t('dashboardMetrics.avgDuration', 'Avg Duracion (min)'),
    //   value: metrics.avgDuration.toFixed(2),
    //   icon: 'clock'
    // },
    {
      id: 'totalCost',
      title: t('dashboardMetrics.totalCost'),
      value: `${metrics.totalCost.toFixed(2)}`,
      icon: 'dollar'
    },
    {
      id: 'costPerCall',
      title: t('dashboardMetrics.costPerCall'),
      value: `${metrics.costPerCall.toFixed(4)}`,
      icon: 'dollar'
    }
  ];

  return (
    <Box margin={{ vertical: 'l' }} padding={{ horizontal: 'l' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '20px' 
      }}>
        {metricCards.map((card) => (
          <MetricCard
            key={card.id}
            title={card.title}
            value={card.value}
            icon={card.icon}
            secondaryIcon={card.secondaryIcon}
            loading={loading}
          />
        ))}
      </div>
    </Box>
  );
};

const MetricCard = ({ title, value, icon, secondaryIcon, loading }) => {
  const { t } = useTranslation();

  return (
    <div style={{ 
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08)',
      padding: '16px 24px',
      minWidth: '200px',
      flex: '1 1 0',
      maxWidth: '260px',
      transform: 'translateY(0)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'default',
      '&:hover': {
        boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15), 0 6px 10px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-3px)'
      }
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '12px' 
      }}>
        <Icon name={icon} />
        {secondaryIcon && (
          <Icon name={secondaryIcon} style={{ marginLeft: '4px' }} />
        )}
        <span style={{ 
          marginLeft: '8px', 
          fontSize: '14px',
          fontWeight: '500' 
        }}>
          {title}
        </span>
      </div>
      <div style={{ 
        fontSize: '28px', 
        fontWeight: 'bold' 
      }}>
        {loading ? t("home.loading") : value}
      </div>
    </div>
  );
};

export default DashboardMetrics;